import React, { useState, useRef, useEffect, useCallback } from "react";
import "../css/tablePageSizeSelect.css";

const DEFAULT_OPTIONS = [5, 10, 15, 20, 50, 100];

const normalizeOption = (opt) => {
  if (typeof opt === "object" && opt !== null) {
    const value = String(opt.value ?? "");
    return {
      value,
      label: opt.label ?? value,
    };
  }

  const value = String(opt);
  return {
    value,
    label: value,
  };
};

/**
 * Custom page-size control; onChange matches a native select (event.target.value).
 */
export default function TablePageSizeSelect({
  value,
  onChange,
  options = DEFAULT_OPTIONS,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) close();
    };
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  const normalizedOptions = options.map(normalizeOption);
  const currentValue = String(value ?? "");
  const selectedOption =
    normalizedOptions.find((opt) => opt.value === currentValue) ||
    normalizedOptions[0];

  const pick = (nextValue) => {
    if (onChange) onChange({ target: { value: String(nextValue) } });
    close();
  };

  return (
    <div
      className={`table-page-size-dd ${className}`.trim()}
      ref={rootRef}
    >
      <button
        type="button"
        className="table-page-size-dd__trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{selectedOption?.label ?? ""}</span>
        <svg
          className="table-page-size-dd__chev"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#2457BF"
          strokeWidth="2.25"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && (
        <ul className="table-page-size-dd__menu" role="listbox">
          {normalizedOptions.map((opt) => (
            <li
              key={opt.value}
              role="option"
              aria-selected={opt.value === currentValue}
              className={opt.value === currentValue ? "is-active" : ""}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(opt.value)}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
